import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useSelector } from "react-redux";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import Preloader from "../../components/CustomComponents/preLoad/preLoader";
import showErrorToast, { showSuccessToast } from "../../components/CustomComponents/toast/toast";
import useDarkMode from "../../hooks/useDarkMode";
import useThemeColor from "../../hooks/useThemeColor";
import swal from "sweetalert";
import IngredientSelectionModal from "../../components/CustomComponents/modal/IngredientSelectionModal";
import { Panel, PanelHeader, PanelBody } from "../../components/panel/panel";

const CreateRecipe = () => {
  const { menuItemId } = useParams();
  const navigate = useNavigate();
  const CompID = useSelector((state) => state.auth.user)?.userData?.compID;
  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();

  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [product, setProduct] = useState(null);
  const [recipeType, setRecipeType] = useState("Made to order");
  const [instructions, setInstructions] = useState("");
  const [expectedYield, setExpectedYield] = useState("");
  const [expectedYieldUnit, setExpectedYieldUnit] = useState("");
  const [ingredients, setIngredients] = useState([]);
  const [availableIngredients, setAvailableIngredients] = useState([]);
  const [units, setUnits] = useState([]);
  const [unitCost, setUnitCost] = useState(0);
  const [showIngredientModal, setShowIngredientModal] = useState(false);
  const hasFetchedRecipeIngredients = useRef(false);

  const getProductDisplayName = (item) => {
    if (!item) return "";
    const v = (p) => (p != null && String(p).trim() !== "" ? String(p).trim() : "");
    return (
      v(item.menuItem_DefaultVariantTitle) ||
      v(item.MenuItem_DefaultVariantTitle) ||
      v(item.menuItem_Name) ||
      v(item.MenuItem_Name) ||
      v(item.menuitem_name) ||
      ""
    );
  };

  // Fetch product details
  const fetchProductDetails = useCallback(async () => {
    if (!menuItemId || !CompID) return;

    try {
      setInitialLoading(true);
      const response = await axiosInstance.get(APIs.GET_ITEM_BY_ID, {
        params: { CompID, MenuItem_ID: menuItemId },
      });

      const itemData = response.data.data || response.data;
      if (itemData) {
        setProduct(itemData);
        setRecipeType(itemData.menuItem_RecipeType || itemData.MenuItem_RecipeType || "Made to order");
        setInstructions(itemData.menuItem_Instructions || itemData.MenuItem_Instructions || "");
        // Handle expected yield - convert to string and handle null/undefined properly
        // Don't use || "" because 0 is a valid value
        const expectedYieldValue = itemData.menuItem_ExpectedYield ?? itemData.MenuItem_ExpectedYield;
        if (expectedYieldValue !== null && expectedYieldValue !== undefined) {
          setExpectedYield(String(expectedYieldValue));
        } else {
          setExpectedYield("");
        }
        // Handle unit - check both camelCase and PascalCase
        const expectedYieldUnitValue = itemData.menuItem_ExpectedYieldUnit ?? itemData.MenuItem_ExpectedYieldUnit;
        setExpectedYieldUnit(expectedYieldUnitValue || "");
      }
    } catch (error) {
      showErrorToast("Failed to load product details");
    } finally {
      setInitialLoading(false);
    }
  }, [menuItemId, CompID]);

  // Fetch recipe ingredients
  const fetchRecipeIngredients = useCallback(async () => {

    if (!menuItemId || !CompID) {
      console.log("Skipping fetchRecipeIngredients - missing params:", { menuItemId, CompID });
      return;
    }

    if (hasFetchedRecipeIngredients.current) {
      console.log("Skipping fetchRecipeIngredients - already fetched");
      return;
    }

    hasFetchedRecipeIngredients.current = true;

    try {
      console.log("Fetching recipe ingredients for MenuItem_ID:", menuItemId, "CompID:", CompID);

      const response = await axiosInstance.get(APIs.GET_RECIPE_INGREDIENTS, {
        params: { MenuItem_ID: menuItemId, CompID },
      });

      console.log("Recipe ingredients response:", response.data);
      const ingredientsData = response.data.data || response.data || [];

      // Enrich ingredients with full product details from database
      const enrichedIngredients = await Promise.all(
        (Array.isArray(ingredientsData) ? ingredientsData : []).map(async (ing) => {
          try {
            // Fetch full product details from database
            const response = await axiosInstance.get(APIs.GET_ITEM_BY_ID, {
              params: { CompID, MenuItem_ID: ing.ingredientID || ing.menuItem_ID },
            });

            const productData = response.data.data || response.data;

            return {
              ...ing,
              id: ing.id || ing.ID || Date.now() + Math.random(),
              ingredientID: ing.ingredientID || ing.IngredientID || ing.menuItem_ID,
              menuItem_ID: ing.ingredientID || ing.IngredientID || ing.menuItem_ID,
              ingredientName: ing.ingredientName || ing.IngredientName || "",
              quantity: ing.quantity || ing.Quantity || "",
              unit: ing.selectedUnit || ing.SelectedUnit || "",
              cost: ing.cost || ing.Cost || "",
              measurementType: productData.menuItem_MeasurementType || ing.measurementType || ing.MeasurementType || "Unit",
              costPrice: productData.menuItem_CostPrice || ing.unitCost || ing.UnitCost || ing.costPrice || 0,
              defaultSize: productData.menuItem_DefaultSize || ing.defaultSize || ing.DefaultSize || 0,
              defaultSizeUnit: productData.menuItem_DefaultSizeUnit || ing.defaultSizeUnit || ing.DefaultSizeUnit || "",
            };
          } catch (error) {
            console.error(`Failed to fetch details for ingredient ${ing.ingredientID || ing.menuItem_ID}:`, error);
            // Fallback to availableIngredients or existing data
            const product = availableIngredients.find(
              (p) => p.menuItem_ID === ing.ingredientID || p.menuItem_ID === ing.menuItem_ID
            );
            return {
              ...ing,
              id: ing.id || ing.ID || Date.now() + Math.random(),
              ingredientID: ing.ingredientID || ing.IngredientID || ing.menuItem_ID,
              menuItem_ID: ing.ingredientID || ing.IngredientID || ing.menuItem_ID,
              ingredientName: ing.ingredientName || ing.IngredientName || "",
              quantity: ing.quantity || ing.Quantity || "",
              unit: ing.selectedUnit || ing.SelectedUnit || "",
              cost: ing.cost || ing.Cost || "",
              measurementType: product?.menuItem_MeasurementType || ing.measurementType || ing.MeasurementType || "Unit",
              costPrice: product?.menuItem_CostPrice || ing.unitCost || ing.UnitCost || ing.costPrice || 0,
              defaultSize: product?.menuItem_DefaultSize || ing.defaultSize || ing.DefaultSize || 0,
              defaultSizeUnit: product?.menuItem_DefaultSizeUnit || ing.defaultSizeUnit || ing.DefaultSizeUnit || "",
            };
          }
        })
      );

      setIngredients(enrichedIngredients);
      console.log("Enriched ingredients set:", enrichedIngredients);
    } catch (error) {
      console.error("Error fetching recipe ingredients:", error);
      console.error("Error details:", error.response?.data || error.message);
      // If endpoint doesn't exist yet, start with empty ingredients
      setIngredients([]);
      hasFetchedRecipeIngredients.current = false; // Reset on error so it can retry
    }
  }, [menuItemId, CompID, availableIngredients]);

  // Fetch available ingredients (all products for ingredient selection)
  const fetchAvailableIngredients = useCallback(async () => {
    if (!CompID) return;

    try {
      // Get all products - ingredients can be any product
      const response = await axiosInstance.get(APIs.GET_ALL_ONLY_PRODUCTS, {
        params: { CompID },
      });
      const productsData = response.data.data || response.data || [];
      const validProducts = Array.isArray(productsData)
        ? productsData.filter((p) => p != null && p.menuItem_ID != null)
        : [];
      setAvailableIngredients(validProducts);
    } catch (error) {
      showErrorToast("Failed to load products");
      setAvailableIngredients([]);
    }
  }, [CompID]);

  // Fetch units
  const fetchUnits = useCallback(async () => {
    try {
      const response = await axiosInstance.get("/api/Unit/SuperAdmin/GetAllUnits", {
        params: {
          SortBy: "MeasureName",
          SortOrder: "ASC",
          Search: "",
          PageSize: "1000",
          PageNumber: 1,
        },
      });

      if (response.data.status) {
        const unitsData = response.data.data || [];
        setUnits(Array.isArray(unitsData) ? unitsData : []);
      }
    } catch (error) {
      showErrorToast("Failed to load units");
    }
  }, []);

  // Reset ref when menuItemId changes
  useEffect(() => {
    hasFetchedRecipeIngredients.current = false;
  }, [menuItemId]);

  // Initial data load
  useEffect(() => {
    fetchProductDetails();
    fetchAvailableIngredients();
    fetchUnits();
  }, [fetchProductDetails, fetchAvailableIngredients, fetchUnits]);

  // Fetch recipe ingredients after product is loaded
  useEffect(() => {
    if (product && product.menuItem_HasRecipe && !hasFetchedRecipeIngredients.current) {
      fetchRecipeIngredients();
    }
  }, [product, fetchRecipeIngredients]);

  // Calculate unit cost from ingredients
  useEffect(() => {
    const totalCost = ingredients.reduce((sum, ing) => sum + (parseFloat(ing.cost) || 0), 0);
    setUnitCost(totalCost);
  }, [ingredients]);

  // Handle adding ingredients from modal
  const handleSelectIngredients = async (selectedProducts) => {
    // Filter out products that already exist
    const productsToAdd = selectedProducts.filter((product) => {
      return !ingredients.some(
        (ing) => ing.ingredientID === product.menuItem_ID || ing.menuItem_ID === product.menuItem_ID
      );
    });

    // Fetch full product details from database for each selected product
    const newIngredients = await Promise.all(
      productsToAdd.map(async (product) => {
        try {
          // Fetch full product details from database
          const response = await axiosInstance.get(APIs.GET_ITEM_BY_ID, {
            params: { CompID, MenuItem_ID: product.menuItem_ID },
          });

          const productData = response.data.data || response.data;

          return {
            id: Date.now() + Math.random(), // Unique ID
            ingredientID: product.menuItem_ID,
            menuItem_ID: product.menuItem_ID,
            ingredientName: getProductDisplayName(productData) || getProductDisplayName(product) || "",
            measurementType: productData.menuItem_MeasurementType || product.menuItem_MeasurementType || "Unit",
            costPrice: productData.menuItem_CostPrice || product.menuItem_CostPrice || 0,
            defaultSize: productData.menuItem_DefaultSize || product.menuItem_DefaultSize || 0,
            defaultSizeUnit: productData.menuItem_DefaultSizeUnit || product.menuItem_DefaultSizeUnit || "",
            quantity: "",
            unit: "",
            cost: "",
          };
        } catch (error) {
          console.error(`Failed to fetch details for product ${product.menuItem_ID}:`, error);
          // Fallback to product data from modal if API call fails
          return {
            id: Date.now() + Math.random(),
            ingredientID: product.menuItem_ID,
            menuItem_ID: product.menuItem_ID,
            ingredientName: getProductDisplayName(product) || "",
            measurementType: product.menuItem_MeasurementType || "Unit",
            costPrice: product.menuItem_CostPrice || 0,
            defaultSize: product.menuItem_DefaultSize || 0,
            defaultSizeUnit: product.menuItem_DefaultSizeUnit || "",
            quantity: "",
            unit: "",
            cost: "",
          };
        }
      })
    );

    setIngredients([...ingredients, ...newIngredients]);
  };

  // Remove ingredient
  const handleRemoveIngredient = (index) => {
    setIngredients(ingredients.filter((_, i) => i !== index));
  };

  // Calculate cost based on quantity, unit, and ingredient's base cost
  const calculateIngredientCost = (ingredient, quantity, selectedUnit) => {
    // Validate required fields
    if (!ingredient || !quantity || !selectedUnit) {
      return "";
    }

    const qty = parseFloat(quantity);
    if (isNaN(qty) || qty <= 0) return "";

    const costPrice = parseFloat(ingredient.costPrice);

    if (isNaN(costPrice) || costPrice <= 0) {
      console.warn("Missing or invalid cost price for ingredient:", ingredient.ingredientName);
      return "";
    }

    // Handle "Unit (Qty)" - simple quantity multiplier
    if (selectedUnit === "Unit (Qty)") {
      const totalCost = costPrice * qty;
      console.log("Cost calculation (Unit Qty):", {
        ingredient: ingredient.ingredientName,
        costPrice,
        quantity: qty,
        totalCost
      });
      return totalCost.toFixed(2);
    }

    // For other units, we need defaultSize and defaultSizeUnit
    if (!ingredient.defaultSize || ingredient.defaultSize === 0) {
      console.warn("Missing default size for ingredient:", ingredient.ingredientName);
      return "";
    }

    if (!ingredient.defaultSizeUnit) {
      console.warn("Missing default size unit for ingredient:", ingredient.ingredientName);
      return "";
    }

    const defaultSize = parseFloat(ingredient.defaultSize);

    if (isNaN(defaultSize) || defaultSize <= 0) {
      return "";
    }

    // Find the selected unit and default size unit
    const selectedUnitObj = units.find((u) =>
      u.measureName?.toLowerCase() === selectedUnit?.toLowerCase() ||
      u.MeasureName?.toLowerCase() === selectedUnit?.toLowerCase()
    );

    const defaultUnitObj = units.find((u) =>
      u.measureName?.toLowerCase() === ingredient.defaultSizeUnit?.toLowerCase() ||
      u.MeasureName?.toLowerCase() === ingredient.defaultSizeUnit?.toLowerCase()
    );

    // If units are the same, simple calculation
    if (selectedUnit?.toLowerCase() === ingredient.defaultSizeUnit?.toLowerCase()) {
      const cost = (costPrice / defaultSize) * qty;
      return cost.toFixed(2);
    }

    // If we don't have both units, can't calculate
    if (!selectedUnitObj || !defaultUnitObj) {
      console.warn("Units not found for conversion:", {
        selectedUnit,
        defaultSizeUnit: ingredient.defaultSizeUnit,
        availableUnits: units.map(u => u.measureName || u.MeasureName)
      });
      return "";
    }

    // Get measurement values
    // Measurement represents: how much of the base unit this represents
    // Example: Liter (base) has Measurement=1, Milliliter has Measurement=0.001
    // This means: 1 Milliliter = 0.001 Liters
    let selectedUnitMeasurement = parseFloat(selectedUnitObj.measurement || selectedUnitObj.Measurement);
    let defaultUnitMeasurement = parseFloat(defaultUnitObj.measurement || defaultUnitObj.Measurement);

    // Validate measurements - they must be valid numbers > 0
    if (isNaN(selectedUnitMeasurement) || selectedUnitMeasurement <= 0) {
      console.warn("Invalid selected unit measurement:", {
        selectedUnit,
        measurement: selectedUnitObj.measurement || selectedUnitObj.Measurement,
        unitObj: selectedUnitObj
      });
      return "";
    }
    if (isNaN(defaultUnitMeasurement) || defaultUnitMeasurement <= 0) {
      console.warn("Invalid default unit measurement:", {
        defaultSizeUnit: ingredient.defaultSizeUnit,
        measurement: defaultUnitObj.measurement || defaultUnitObj.Measurement,
        unitObj: defaultUnitObj
      });
      return "";
    }

    // BIDIRECTIONAL MEASUREMENT CALCULATION:
    // Step 1: Convert selected quantity to base unit
    // Formula: quantity * Measurement = base unit value
    // Example: 1 Liter * 1 = 1 base unit
    const selectedQuantityInBaseUnit = qty * selectedUnitMeasurement;

    // Step 2: Convert from base unit to default size unit
    // Formula: base unit value / Measurement = quantity in target unit
    // Example: 1 base unit / 0.001 = 1000 Milliliters
    const selectedQuantityInDefaultUnit = selectedQuantityInBaseUnit / defaultUnitMeasurement;

    // Now calculate cost based on default size unit
    // Example: Milk costs 100 for 500ml
    // If user selected 1 Liter = 1000ml
    // Cost = (100 / 500) * 1000 = 200
    const costPerDefaultUnit = costPrice / defaultSize;
    const totalCost = costPerDefaultUnit * selectedQuantityInDefaultUnit;

    // Debug logging
    console.log("Cost calculation:", {
      ingredient: ingredient.ingredientName,
      costPrice,
      defaultSize,
      defaultSizeUnit: ingredient.defaultSizeUnit,
      defaultUnitMeasurement,
      selectedUnit,
      quantity: qty,
      selectedUnitMeasurement,
      selectedQuantityInBaseUnit,
      selectedQuantityInDefaultUnit,
      costPerDefaultUnit,
      totalCost
    });

    return totalCost.toFixed(2);
  };

  // Update ingredient
  const handleIngredientChange = (index, field, value) => {
    const updated = [...ingredients];
    updated[index] = { ...updated[index], [field]: value };

    // Auto-calculate cost when quantity or unit changes
    if (field === "quantity" || field === "unit") {
      const ingredient = updated[index];
      const quantity = field === "quantity" ? value : ingredient.quantity;
      const selectedUnit = field === "unit" ? value : ingredient.unit;

      const calculatedCost = calculateIngredientCost(ingredient, quantity, selectedUnit);
      if (calculatedCost) {
        updated[index].cost = calculatedCost;
      }
    }

    setIngredients(updated);
  };

  // Save recipe
  const handleSave = async () => {
    if (!product) return;

    try {
      setLoading(true);

      // Get current item details
      const itemResponse = await axiosInstance.get(APIs.GET_ITEM_BY_ID, {
        params: { CompID, MenuItem_ID: menuItemId },
      });
      const itemData = itemResponse.data.data || itemResponse.data;
      const g = (c, p, def) => (itemData == null ? def : (itemData[c] ?? itemData[p] ?? def));

      // Calculate total cost from ingredients
      const calculatedCostPrice = ingredients.reduce((sum, ing) => sum + (parseFloat(ing.cost) || 0), 0);

      // Preserve previous inventory/track state; only change recipe-related fields
      const updateData = {
        menuItem_ID: parseInt(menuItemId),
        compID: CompID,
        current_Quantity: parseFloat(g("current_Quantity", "Current_Quantity", 0)) || 0,
        reorder_Quantity: parseFloat(g("reorder_Quantity", "Reorder_Quantity", 0)) || 0,
        reorder_Level: parseFloat(g("reorder_Level", "Reorder_Level", 0)) || 0,
        availableQuantity: g("availableQuantity", "AvailableQuantity", null) ?? null,
        orderQuantityLimit: parseInt(g("orderQuantityLimit", "OrderQuantityLimit", 0), 10) || 0,
        MenuItem_IsBuyingProduct: !!g("menuItem_IsBuyingProduct", "MenuItem_IsBuyingProduct", false),
        MenuItem_IsStockingProduct: !!g("menuItem_IsStockingProduct", "MenuItem_IsStockingProduct", false),
        MenuItem_SourceType: g("menuItem_SourceType", "MenuItem_SourceType", "Made here") || "Made here",
        MenuItem_HasRecipe: true,
        MenuItem_RecipeId: parseInt(g("menuItem_RecipeId", "MenuItem_RecipeId", menuItemId), 10) || menuItemId,
        MenuItem_RecipeType: recipeType,
        MenuItem_Instructions: instructions,
        MenuItem_ExpectedYield: recipeType === "Made in batches" && expectedYield ? parseFloat(expectedYield) : null,
        MenuItem_ExpectedYieldUnit: recipeType === "Made in batches" && expectedYieldUnit ? expectedYieldUnit : null,
        MenuItem_IsTrackBatch: !!g("menuItem_IsTrackBatch", "MenuItem_IsTrackBatch", false),
        MenuItem_IsTrackStock: !!g("menuItem_IsTrackStock", "MenuItem_IsTrackStock", false),
        MenuItem_SellingMethodBoth: !!g("menuItem_SellingMethodBoth", "MenuItem_SellingMethodBoth", false),
        MenuItem_CostPrice: recipeType === "Made to order" && calculatedCostPrice > 0 ? calculatedCostPrice : (g("menuItem_CostPrice", "MenuItem_CostPrice", null) ?? null),
        MenuItem_PurchaseContainerType: g("menuItem_PurchaseContainerType", "MenuItem_PurchaseContainerType", "Individual") || "Individual",
        MenuItem_PurchaseContainerQty: parseInt(g("menuItem_PurchaseContainerQty", "MenuItem_PurchaseContainerQty", 1), 10) || 1,
      };

      const response = await axiosInstance.put(APIs.PUT_UPDATE_ITEM_INVENTORY, updateData);

      if (response.data.status) {
        // Save recipe ingredients
        if (ingredients.length > 0) {
          try {
            // First, delete existing ingredients for this recipe
            await axiosInstance.delete(APIs.DELETE_RECIPE_INGREDIENTS, {
              params: { RecipeID: parseInt(menuItemId), CompID },
            });

            // Prepare ingredients data with all required fields
            const ingredientsToSave = ingredients.map((ing) => {
              // Calculate quantity in base unit
              let quantityInBaseUnit = 0;

              if (ing.unit === "Unit (Qty)") {
                // For Unit (Qty), quantity in base unit is the same as quantity
                quantityInBaseUnit = parseFloat(ing.quantity) || 0;
              } else if (ing.unit && ing.quantity) {
                // Find the selected unit to get its Measurement
                const selectedUnitObj = units.find(
                  (u) =>
                    u.measureName?.toLowerCase() === ing.unit?.toLowerCase() ||
                    u.MeasureName?.toLowerCase() === ing.unit?.toLowerCase()
                );

                if (selectedUnitObj) {
                  const unitMeasurement = parseFloat(
                    selectedUnitObj.measurement || selectedUnitObj.Measurement || 1
                  );
                  // Convert to base unit: quantity * Measurement
                  quantityInBaseUnit = (parseFloat(ing.quantity) || 0) * unitMeasurement;
                } else {
                  quantityInBaseUnit = parseFloat(ing.quantity) || 0;
                }
              }

              return {
                RecipeID: parseInt(menuItemId),
                CompID: CompID,
                IngredientID: ing.ingredientID || ing.menuItem_ID,
                IngredientName: ing.ingredientName || "",
                Quantity: parseFloat(ing.quantity) || 0,
                SelectedUnit: ing.unit || "",
                QuantityInBaseUnit: quantityInBaseUnit,
                Cost: parseFloat(ing.cost) || 0,
                UnitCost: parseFloat(ing.costPrice) || 0,
                DefaultSize: parseFloat(ing.defaultSize) || 0,
                DefaultSizeUnit: ing.defaultSizeUnit || "",
                MeasurementType: ing.measurementType || "Unit",
              };
            });

            // Save all ingredients
            const ingredientsResponse = await axiosInstance.post(
              APIs.POST_ADD_RECIPE_INGREDIENTS,
              ingredientsToSave
            );

            if (ingredientsResponse.data.status) {
              showSuccessToast("Recipe and ingredients saved successfully");
            } else {
              showSuccessToast("Recipe saved, but some ingredients failed to save");
            }
          } catch (ingredientError) {
            console.error("Error saving ingredients:", ingredientError);
            showSuccessToast("Recipe saved, but failed to save ingredients");
          }
        } else {
          // No ingredients, just delete existing ones
          try {
            await axiosInstance.delete(APIs.DELETE_RECIPE_INGREDIENTS, {
              params: { RecipeID: parseInt(menuItemId), CompID },
            });
          } catch (deleteError) {
            console.error("Error deleting ingredients:", deleteError);
          }
          showSuccessToast("Recipe saved successfully");
        }

        navigate("/recipes/manageRecipes");
      } else {
        showErrorToast(response.data.message || "Failed to save recipe");
      }
    } catch (error) {
      showErrorToast("Failed to save recipe");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Delete recipe
  const handleDelete = async () => {
    if (!product) return;

    try {
      const confirmDelete = await swal({
        title: "Are you sure?",
        text: "Are you sure you want to delete this recipe?",
        icon: "warning",
        buttons: true,
        dangerMode: true,
      });

      if (!confirmDelete) return;

      setLoading(true);

      // Get current item details
      const itemResponse = await axiosInstance.get(APIs.GET_ITEM_BY_ID, {
        params: { CompID, MenuItem_ID: menuItemId },
      });
      const itemData = itemResponse.data.data || itemResponse.data;
      const g = (c, p, def) => (itemData == null ? def : (itemData[c] ?? itemData[p] ?? def));

      // Preserve previous inventory/track state; only clear recipe-related fields
      const updateData = {
        menuItem_ID: parseInt(menuItemId),
        compID: CompID,
        current_Quantity: parseFloat(g("current_Quantity", "Current_Quantity", 0)) || 0,
        reorder_Quantity: parseFloat(g("reorder_Quantity", "Reorder_Quantity", 0)) || 0,
        reorder_Level: parseFloat(g("reorder_Level", "Reorder_Level", 0)) || 0,
        availableQuantity: g("availableQuantity", "AvailableQuantity", null) ?? null,
        orderQuantityLimit: parseInt(g("orderQuantityLimit", "OrderQuantityLimit", 0), 10) || 0,
        MenuItem_IsBuyingProduct: !!g("menuItem_IsBuyingProduct", "MenuItem_IsBuyingProduct", false),
        MenuItem_IsStockingProduct: !!g("menuItem_IsStockingProduct", "MenuItem_IsStockingProduct", false),
        MenuItem_SourceType: g("menuItem_SourceType", "MenuItem_SourceType", "Made here") || "Made here",
        MenuItem_HasRecipe: false,
        MenuItem_RecipeId: null,
        MenuItem_RecipeType: "Made to order",
        MenuItem_Instructions: "",
        MenuItem_IsTrackBatch: !!g("menuItem_IsTrackBatch", "MenuItem_IsTrackBatch", false),
        MenuItem_IsTrackStock: !!g("menuItem_IsTrackStock", "MenuItem_IsTrackStock", false),
        MenuItem_SellingMethodBoth: !!g("menuItem_SellingMethodBoth", "MenuItem_SellingMethodBoth", false),
        MenuItem_CostPrice: g("menuItem_CostPrice", "MenuItem_CostPrice", null) ?? null,
        MenuItem_PurchaseContainerType: g("menuItem_PurchaseContainerType", "MenuItem_PurchaseContainerType", "Individual") || "Individual",
        MenuItem_PurchaseContainerQty: parseInt(g("menuItem_PurchaseContainerQty", "MenuItem_PurchaseContainerQty", 1), 10) || 1,
      };

      const response = await axiosInstance.put(APIs.PUT_UPDATE_ITEM_INVENTORY, updateData);

      if (response.data.status) {
        // Delete recipe ingredients
        try {
          await axiosInstance.delete(APIs.DELETE_RECIPE_INGREDIENTS, {
            params: { RecipeID: parseInt(menuItemId), CompID },
          });
        } catch (ingredientError) {
          console.error("Error deleting ingredients:", ingredientError);
        }

        showSuccessToast("Recipe deleted successfully");
        navigate("/recipes/manageRecipes");
      } else {
        showErrorToast(response.data.message || "Failed to delete recipe");
      }
    } catch (error) {
      showErrorToast("Failed to delete recipe");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return <Preloader />;
  }

  if (!product) {
    return (
      <div className="container-fluid p-3">
        <div className="alert alert-danger">Product not found</div>
      </div>
    );
  }

  const isUpdateRecipe = !!(product?.menuItem_HasRecipe ?? product?.MenuItem_HasRecipe);
  const pageLabel = isUpdateRecipe ? "Update Recipe" : "Create Recipe";

  return (
    <div className="container-fluid p-3">
      {loading && <Preloader />}

      {/* Breadcrumb */}
      <div className="d-flex align-items-center justify-content-between mb-2 flex-wrap gap-2">
        <div>
          <ol className="breadcrumb mb-1">
            <li className="breadcrumb-item">
              <Link to="/">Home</Link>
            </li>
            <li className="breadcrumb-item">
              <Link to="/recipes/manageRecipes">Manage Recipes</Link>
            </li>
            <li className="breadcrumb-item active">{pageLabel}</li>
          </ol>
          <h1 className="page-header mb-0" style={{ fontSize: "1.35rem" }}>{pageLabel}</h1>
        </div>

        {/* Action Buttons - Top */}
        <div className="d-flex gap-2 align-items-center">
          {isUpdateRecipe && (
            <button
              type="button"
              className="btn btn-link text-danger p-0"
              onClick={handleDelete}
              style={{
                textDecoration: "none",
                color: "#dc3545",
                fontWeight: "600",
                fontSize: "13px",
                marginRight: "8px",
              }}
            >
              Delete recipe
            </button>
          )}
          <button
            type="button"
            className="btn"
            onClick={() => navigate("/recipes/manageRecipes")}
            style={{
              backgroundColor: isDarkMode ? "#404040" : "#6c757d",
              color: "white",
              border: "none",
              padding: "6px 16px",
              borderRadius: "8px",
              fontSize: "13px",
              fontWeight: "600",
            }}
          >
            Cancel
          </button>
            <button
              type="button"
              className="btn"
              onClick={handleSave}
              disabled={loading}
              style={{
                backgroundColor: themeColor,
                color: "white",
                border: "none",
                padding: "6px 16px",
                borderRadius: "8px",
                fontSize: "13px",
                fontWeight: "600",
                opacity: loading ? 0.6 : 1,
              }}
            >
              {loading ? "Saving..." : "Save Recipe"}
            </button>
        </div>
      </div>

      {/* Recipe Panel - Product info + Recipe details in one panel */}
      <Panel>
        <PanelHeader
          style={{
            backgroundColor: themeColor,
            color: "white",
            border: "none",
            borderRadius: "10px 10px 0 0",
            padding: "12px 20px",
            fontWeight: "600",
            fontSize: "15px",
          }}
        >
          <i className="fa fa-utensils me-2"></i>
          Recipe
        </PanelHeader>
        <PanelBody
          style={{
            padding: "16px 20px",
            backgroundColor: isDarkMode ? "#2d3436" : "#ffffff",
            borderRadius: "0 0 10px 10px",
            border: `1px solid ${themeColor}20`,
          }}
        >
          {/* Product info (Name, SKU) */}
          <div className="row g-2 mb-3 pb-3" style={{ borderBottom: isDarkMode ? "1px solid #404040" : "1px solid #e9ecef" }}>
            <div className="col-md-6">
              <p className="mb-0" style={{ fontSize: "13px", color: isDarkMode ? "#adb5bd" : "#6c757d" }}>
                <strong style={{ color: isDarkMode ? "#ffffff" : "#000000" }}>Name:</strong>{" "}
                {getProductDisplayName(product)}
              </p>
            </div>
            <div className="col-md-6">
              {product.menuItem_SKU && (
                <p className="mb-0" style={{ fontSize: "13px", color: isDarkMode ? "#adb5bd" : "#6c757d" }}>
                  <strong style={{ color: isDarkMode ? "#ffffff" : "#000000" }}>SKU:</strong>{" "}
                  {product.menuItem_SKU}
                </p>
              )}
            </div>
          </div>

          {/* Recipe Type Toggle */}
          <div className="mb-3">
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontWeight: "600",
                fontSize: "14px",
                color: isDarkMode ? "#ffffff" : "#000000",
              }}
            >
              Recipe type
            </label>
            <div className="d-flex gap-2">
              <button
                type="button"
                onClick={() => setRecipeType("Made to order")}
                style={{
                  flex: 1,
                  padding: "10px 20px",
                  fontSize: "13px",
                  borderRadius: "8px",
                  border: `2px solid ${recipeType === "Made to order" ? themeColor : isDarkMode ? "#404040" : "#dee2e6"
                    }`,
                  backgroundColor:
                    recipeType === "Made to order"
                      ? themeColor
                      : isDarkMode
                        ? "#1e1e1e"
                        : "#ffffff",
                  color:
                    recipeType === "Made to order"
                      ? "#ffffff"
                      : isDarkMode
                        ? "#ffffff"
                        : "#000000",
                  fontWeight: "600",
                  transition: "all 0.2s ease",
                  cursor: "pointer",
                }}
              >
                Made to order
              </button>
              <button
                type="button"
                onClick={() => setRecipeType("Made in batches")}
                style={{
                  flex: 1,
                  padding: "10px 20px",
                  fontSize: "13px",
                  borderRadius: "8px",
                  border: `2px solid ${recipeType === "Made in batches"
                      ? themeColor
                      : isDarkMode
                        ? "#404040"
                        : "#dee2e6"
                    }`,
                  backgroundColor:
                    recipeType === "Made in batches"
                      ? themeColor
                      : isDarkMode
                        ? "#1e1e1e"
                        : "#ffffff",
                  color:
                    recipeType === "Made in batches"
                      ? "#ffffff"
                      : isDarkMode
                        ? "#ffffff"
                        : "#000000",
                  fontWeight: "600",
                  transition: "all 0.2s ease",
                  cursor: "pointer",
                }}
              >
                Made in batches
              </button>
            </div>

            {/* Expected Yield - Only show for "Made in batches" */}
            {recipeType === "Made in batches" && (
              <div className="mt-3">
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontWeight: "600",
                    fontSize: "14px",
                    color: isDarkMode ? "#ffffff" : "#000000",
                  }}
                >
                  Expected yield
                </label>
                <div className="d-flex gap-2 align-items-center">
                  <input
                    type="number"
                    className="form-control"
                    placeholder="0"
                    value={expectedYield}
                    onChange={(e) => setExpectedYield(e.target.value)}
                    step="0.01"
                    style={{
                      width: "120px",
                      backgroundColor: isDarkMode ? "#2d3436" : "#ffffff",
                      border: `1px solid ${isDarkMode ? "#404040" : "#dee2e6"}`,
                      color: isDarkMode ? "#ffffff" : "#000000",
                      borderRadius: "8px",
                      padding: "8px 12px",
                    }}
                  />
                  <select
                    className="form-select"
                    value={expectedYieldUnit}
                    onChange={(e) => setExpectedYieldUnit(e.target.value)}
                    style={{
                      width: "150px",
                      backgroundColor: isDarkMode ? "#2d3436" : "#ffffff",
                      border: `1px solid ${isDarkMode ? "#404040" : "#dee2e6"}`,
                      color: isDarkMode ? "#ffffff" : "#000000",
                      borderRadius: "8px",
                      padding: "8px 12px",
                    }}
                  >
                    <option value="">Select unit</option>
                    {(() => {
                      // Filter units based on product's measurement type
                      const productMeasurementType = product?.menuItem_MeasurementType || "Unit";
                      let filteredUnits = units;

                      if (productMeasurementType) {
                        // Map measurement types: "Weight" -> "Weight", "Volume" -> "Volume", "Unit" -> "Unit"
                        filteredUnits = units.filter((unit) => {
                          // Check both possible property names (camelCase and PascalCase)
                          const unitType = unit.measurementTypeName || unit.MeasurementTypeName || "Unit";
                          // Case-insensitive comparison
                          // If unit has no measurement type, default to "Unit"
                          return unitType.toLowerCase() === productMeasurementType.toLowerCase();
                        });
                      }

                      // If no filtered units found, try to show "Unit" type units as fallback
                      if (filteredUnits.length === 0) {
                        filteredUnits = units.filter((unit) => {
                          const unitType = unit.measurementTypeName || unit.MeasurementTypeName || "Unit";
                          return unitType.toLowerCase() === "unit";
                        });
                      }

                      // If still no units found, show all units
                      if (filteredUnits.length === 0) {
                        filteredUnits = units;
                      }

                      // Check if the selected unit exists in filtered units, if not, add it
                      const selectedUnitExists = filteredUnits.some(
                        (unit) =>
                          (unit.measureName || unit.MeasureName || "").toLowerCase() ===
                          (expectedYieldUnit || "").toLowerCase()
                      );

                      // If selected unit doesn't exist in filtered list but exists in all units, include it
                      if (!selectedUnitExists && expectedYieldUnit) {
                        const matchingUnit = units.find(
                          (unit) =>
                            (unit.measureName || unit.MeasureName || "").toLowerCase() ===
                            expectedYieldUnit.toLowerCase()
                        );
                        if (matchingUnit) {
                          filteredUnits = [matchingUnit, ...filteredUnits];
                        }
                      }

                      return filteredUnits.map((unit) => {
                        const unitName = unit.measureName || unit.MeasureName || "";
                        return (
                          <option key={unit.measureID} value={unitName}>
                            {unitName}
                          </option>
                        );
                      });
                    })()}
                  </select>
                </div>
              </div>
            )}
          </div>

          {/* Ingredients Section */}
          <div className="mb-3">
            <div
              className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2 p-2 rounded"
              style={{
                backgroundColor: isDarkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)",
                borderLeft: `4px solid ${themeColor}`,
              }}
            >
              <h6 style={{ color: isDarkMode ? "#ffffff" : "#000000", margin: 0, fontWeight: "600", fontSize: "14px" }}>
                <i className="fa fa-list-ul me-2" style={{ opacity: 0.9 }}></i>
                Ingredients
              </h6>
              <span
                style={{
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "white",
                  backgroundColor: themeColor,
                  padding: "6px 12px",
                  borderRadius: "8px",
                }}
              >
                Unit Cost: ${unitCost.toFixed(2)}
              </span>
            </div>

            {/* Ingredients Table */}
            {ingredients.length === 0 ? (
              <div
                className="text-center py-4 rounded"
                style={{
                  borderRadius: "8px",
                  backgroundColor: isDarkMode ? "rgba(255,255,255,0.03)" : "#f8f9fa",
                  border: `1px dashed ${isDarkMode ? "#404040" : "#dee2e6"}`,
                }}
              >
                <i className="fa fa-flask" style={{ fontSize: "28px", color: isDarkMode ? "#555" : "#adb5bd", marginBottom: "8px" }}></i>
                <p style={{ color: isDarkMode ? "#adb5bd" : "#6c757d", margin: 0, fontSize: "13px" }}>
                  No ingredients added yet. Click "Add ingredient" to get started.
                </p>
              </div>
            ) : (
              <div className="mb-3 overflow-auto rounded" style={{ border: `1px solid ${isDarkMode ? "#404040" : "#e9ecef"}`, borderRadius: "8px" }}>
                <table
                  className="table table-hover mb-0"
                  style={{
                    backgroundColor: isDarkMode ? "#25282c" : "#ffffff",
                    fontSize: "13px",
                  }}
                >
                  <thead>
                    <tr style={{ backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa", borderBottom: `2px solid ${themeColor}` }}>
                      <th style={{ color: isDarkMode ? "#ffffff" : "#000000", fontWeight: "600", padding: "10px 12px", border: "none" }}>Name</th>
                      <th style={{ color: isDarkMode ? "#ffffff" : "#000000", fontWeight: "600", padding: "10px 12px", border: "none", width: "140px" }}>Quantity</th>
                      <th style={{ color: isDarkMode ? "#ffffff" : "#000000", fontWeight: "600", padding: "10px 12px", border: "none", width: "160px" }}>Unit</th>
                      <th style={{ color: isDarkMode ? "#ffffff" : "#000000", fontWeight: "600", padding: "10px 12px", border: "none", width: "140px" }}>Cost</th>
                      <th style={{ color: isDarkMode ? "#ffffff" : "#000000", fontWeight: "600", padding: "10px 12px", border: "none", width: "70px", textAlign: "center" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ingredients.map((ingredient, index) => (
                      <tr
                        key={ingredient.id || index}
                        style={{
                          borderBottom: `1px solid ${isDarkMode ? "#3a3a3a" : "#eee"}`,
                          transition: "background-color 0.15s ease",
                        }}
                      >
                        <td
                          style={{
                            padding: "10px 12px",
                            color: isDarkMode ? "#ffffff" : "#000000",
                            fontWeight: "600",
                            verticalAlign: "middle",
                          }}
                        >
                          {ingredient.ingredientName || "Unnamed Ingredient"}
                        </td>
                        <td style={{ padding: "8px 12px", verticalAlign: "middle" }}>
                          <input
                            type="number"
                            className="form-control form-control-sm"
                            placeholder="0.00"
                            value={ingredient.quantity}
                            onChange={(e) =>
                              handleIngredientChange(index, "quantity", e.target.value)
                            }
                            step="0.01"
                            style={{
                              backgroundColor: isDarkMode ? "#2d3436" : "#ffffff",
                              border: `1px solid ${isDarkMode ? "#404040" : "#dee2e6"}`,
                              color: isDarkMode ? "#ffffff" : "#000000",
                              borderRadius: "6px",
                              padding: "6px 10px",
                              maxWidth: "120px",
                            }}
                          />
                        </td>
                        <td style={{ padding: "8px 12px", verticalAlign: "middle" }}>
                          <select
                            className="form-select form-select-sm"
                            value={ingredient.unit}
                            onChange={(e) => handleIngredientChange(index, "unit", e.target.value)}
                            style={{
                              backgroundColor: isDarkMode ? "#2d3436" : "#ffffff",
                              border: `1px solid ${isDarkMode ? "#404040" : "#dee2e6"}`,
                              color: isDarkMode ? "#ffffff" : "#000000",
                              borderRadius: "6px",
                              padding: "6px 10px",
                              maxWidth: "150px",
                            }}
                          >
                            <option value="">Select unit</option>
                            {/* Always show Unit (Qty) option */}
                            <option value="Unit (Qty)">Unit (Qty)</option>
                            {(() => {
                              // Filter units based on ingredient's measurement type
                              // Default to "Unit" if measurement type is not found
                              const measurementType = ingredient.measurementType || "Unit";
                              let filteredUnits = units;

                              if (measurementType) {
                                // Map measurement types: "Weight" -> "Weight", "Volume" -> "Volume", "Unit" -> "Unit"
                                filteredUnits = units.filter((unit) => {
                                  // Check both possible property names (camelCase and PascalCase)
                                  const unitType = unit.measurementTypeName || unit.MeasurementTypeName || "Unit";
                                  // Case-insensitive comparison
                                  // If unit has no measurement type, default to "Unit"
                                  return unitType.toLowerCase() === measurementType.toLowerCase();
                                });
                              }

                              // If no filtered units found, try to show "Unit" type units as fallback
                              if (filteredUnits.length === 0) {
                                filteredUnits = units.filter((unit) => {
                                  const unitType = unit.measurementTypeName || unit.MeasurementTypeName || "Unit";
                                  return unitType.toLowerCase() === "unit";
                                });
                              }

                              // If still no units found, show all units
                              if (filteredUnits.length === 0) {
                                filteredUnits = units;
                              }

                              return filteredUnits.map((unit) => (
                                <option key={unit.measureID} value={unit.measureName}>
                                  {unit.measureName}
                                </option>
                              ));
                            })()}
                          </select>
                        </td>
                        <td style={{ padding: "8px 12px", verticalAlign: "middle" }}>
                          <input
                            type="number"
                            className="form-control form-control-sm"
                            placeholder="0.00"
                            value={ingredient.cost}
                            onChange={(e) => handleIngredientChange(index, "cost", e.target.value)}
                            step="0.01"
                            style={{
                              backgroundColor: isDarkMode ? "#2d3436" : "#ffffff",
                              border: `1px solid ${isDarkMode ? "#404040" : "#dee2e6"}`,
                              color: isDarkMode ? "#ffffff" : "#000000",
                              borderRadius: "6px",
                              padding: "6px 10px",
                              maxWidth: "120px",
                            }}
                          />
                        </td>
                        <td style={{ padding: "8px 12px", textAlign: "center", verticalAlign: "middle" }}>
                          <button
                            type="button"
                            className="btn btn-sm p-2 rounded"
                            onClick={() => handleRemoveIngredient(index)}
                            title="Remove ingredient"
                            style={{
                              backgroundColor: "transparent",
                              border: "none",
                              color: "#dc3545",
                              transition: "background-color 0.15s ease",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = "rgba(220, 53, 69, 0.15)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = "transparent";
                            }}
                          >
                            <i className="fa fa-trash-alt" style={{ fontSize: "13px" }}></i>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Add Ingredient Button */}
            <button
              type="button"
              className="btn d-inline-flex align-items-center"
              onClick={() => setShowIngredientModal(true)}
              style={{
                backgroundColor: "transparent",
                color: themeColor,
                border: `2px solid ${themeColor}`,
                padding: "8px 16px",
                borderRadius: "8px",
                fontSize: "13px",
                fontWeight: "600",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = themeColor;
                e.currentTarget.style.color = "white";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "transparent";
                e.currentTarget.style.color = themeColor;
              }}
            >
              <i className="fa fa-plus me-2"></i>
              Add ingredient
            </button>
          </div>

          {/* Instructions Section */}
          <div className="mb-3">
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontWeight: "600",
                fontSize: "14px",
                color: isDarkMode ? "#ffffff" : "#000000",
              }}
            >
              Instructions
            </label>
            <textarea
              className="form-control"
              rows="5"
              placeholder="Enter the instructions"
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              style={{
                backgroundColor: isDarkMode ? "#1e1e1e" : "#ffffff",
                border: `1px solid ${isDarkMode ? "#404040" : "#dee2e6"}`,
                color: isDarkMode ? "#ffffff" : "#000000",
                borderRadius: "8px",
                resize: "vertical",
                fontSize: "13px",
              }}
            />
          </div>
        </PanelBody>
      </Panel>

      {/* Ingredient Selection Modal */}
      <IngredientSelectionModal
        isOpen={showIngredientModal}
        onClose={() => setShowIngredientModal(false)}
        onSelectIngredients={handleSelectIngredients}
        compID={CompID}
        existingIngredients={ingredients}
      />
    </div>
  );
};

export default CreateRecipe;

